
import React, { useMemo, useState } from 'react';
import { DiscussionThread, ArchiveFile, User, Group, Post, Bookmark, BookmarkCategory } from '../types';
import CommunitySidebar from './CommunitySidebar';

interface SidebarProps {
  activePage: 'wiki' | 'chat' | 'profile' | 'community' | 'readings';
  onCategorySelect: (category: string) => void;
  activeCategory: string;
  threads: DiscussionThread[];
  activeThreadId?: string;
  onThreadSelect: (thread: DiscussionThread) => void;
  onThreadDelete: (threadId: string) => void;
  onNewChat: () => void;
  onThreadTitleUpdate: (threadId: string, newTitle: string) => void;
  isAuthenticated?: boolean;
  allFiles?: ArchiveFile[];
  onFileSelect?: (file: ArchiveFile) => void;
  onFileDelete?: (fileId: string) => void;
  selectedFileId?: string;
  user?: User | null;
  // State lifted from Sidebar
  expandedItems: Set<string>;
  onToggleExpand: (id: string) => void;
  sidebarSearch: string;
  onSearchChange: (val: string) => void;
  // Community Props
  communityGroups?: Group[];
  communityFilter?: 'All' | 'Update' | 'Question' | 'Insight' | 'Media';
  communitySelectedGroupId?: string | null;
  communityPosts?: Post[];
  onCommunityFilterChange?: (filter: 'All' | 'Update' | 'Question' | 'Insight' | 'Media') => void;
  onCommunityGroupSelect?: (groupId: string | null) => void;
  onCommunityCreateGroup?: () => void;
  onCommunityDiscoverGroups?: () => void;
  // Bookmark Props
  bookmarks?: Bookmark[];
  bookmarkCategories?: BookmarkCategory[];
  onOpenBookmarkDialog?: (title: string, fileId: string, fileName: string, paragraphId?: string, paragraphText?: string) => void;
  onUpdateCategory?: (id: string, newName: string) => void;
  onDeleteCategory?: (id: string) => void;
  onNavigate?: (path: string) => void;
}

const SidebarBookmarkCategory: React.FC<{
    category: BookmarkCategory;
    bookmarks: Bookmark[];
    allCategories: BookmarkCategory[];
    expandedItems: Set<string>;
    onToggleExpand: (id: string) => void;
    onFileSelect?: (file: ArchiveFile) => void;
    onCategorySelect?: (category: string) => void;
    onUpdateCategory?: (id: string, newName: string) => void;
    onDeleteCategory?: (id: string) => void;
    allFiles?: ArchiveFile[];
}> = ({ category, bookmarks, allCategories, expandedItems, onToggleExpand, onFileSelect, onCategorySelect, onUpdateCategory, onDeleteCategory, allFiles }) => {
    const subCategories = allCategories.filter(c => c.parentId === category.id);
    const categoryBookmarks = bookmarks.filter(b => b.categoryPath === category.path);
    const isExpanded = expandedItems.has(category.id);
    
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(category.name);
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

    const handleCategoryClick = () => {
        onToggleExpand(category.id);
        if (onCategorySelect) {
            onCategorySelect(category.path);
        }
    };

    const handleBookmarkClick = (bm: Bookmark) => {
        const file = allFiles?.find(f => f.id === bm.fileId);
        if (file && onFileSelect) {
            onFileSelect(file);
        }
    };

    const handleRename = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsEditing(true);
    };

    const handleConfirmRename = (e: React.FormEvent) => {
        e.preventDefault();
        if (editName.trim() && editName !== category.name && onUpdateCategory) {
            onUpdateCategory(category.id, editName.trim());
        }
        setIsEditing(false);
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsConfirmingDelete(true);
    };

    const handleConfirmDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onDeleteCategory) onDeleteCategory(category.id);
        setIsConfirmingDelete(false);
    };

    return (
        <div className="mb-0.5">
            <div className="group relative flex items-center">
                {isEditing ? (
                    <form onSubmit={handleConfirmRename} className="flex-1 flex gap-1 px-3 py-1 bg-white dark:bg-stone-800 rounded-lg border border-amber-500 m-0.5">
                        <input 
                            autoFocus
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onBlur={() => setIsEditing(false)}
                            className="flex-1 bg-transparent text-[0.6875rem] outline-none min-w-0"
                        />
                        <button type="submit" className="text-amber-500 hover:text-amber-600"><i className="fa-solid fa-check text-[10px]"></i></button>
                    </form>
                ) : isConfirmingDelete ? (
                    <div className="flex-1 flex items-center justify-between px-3 py-2 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-200 dark:border-red-900/30 m-0.5">
                        <span className="text-[0.625rem] font-bold text-red-600 dark:text-red-400">Delete folder?</span>
                        <div className="flex gap-2">
                             <button onClick={handleConfirmDelete} className="text-[0.625rem] font-black text-red-700 dark:text-red-300">YES</button>
                             <button onClick={(e) => { e.stopPropagation(); setIsConfirmingDelete(false); }} className="text-[0.625rem] font-bold text-stone-400">NO</button>
                        </div>
                    </div>
                ) : (
                    <>
                        <button 
                            onClick={handleCategoryClick}
                            className={`flex-1 flex items-center justify-between px-3 py-2 text-[0.6875rem] transition-all rounded-lg ${isExpanded ? 'text-amber-600 font-bold bg-amber-50/50 dark:bg-amber-900/10' : 'text-stone-500 hover:text-stone-800 dark:hover:text-stone-200 hover:bg-stone-200/30 dark:hover:bg-white/5'} transition-colors`}
                        >
                            <div className="flex items-center gap-2 truncate">
                                <i className={`fa-solid ${isExpanded ? 'fa-folder-open pointer-events-none' : 'fa-folder opacity-50'} text-[0.625rem]`}></i>
                                <span className="truncate">{category.name}</span>
                            </div>
                            <i className={`fa-solid fa-chevron-down text-[0.5rem] transition-transform ${isExpanded ? 'rotate-180' : ''}`}></i>
                        </button>
                        
                        <div className="absolute right-8 top-1/2 -translate-y-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={handleRename} className="p-1 px-1.5 text-stone-400 hover:text-amber-500 transition-colors" title="Rename"><i className="fa-solid fa-pen text-[9px]"></i></button>
                            <button onClick={handleDelete} className="p-1 px-1.5 text-stone-400 hover:text-red-500 transition-colors" title="Delete"><i className="fa-solid fa-circle-xmark text-[9px]"></i></button>
                        </div>
                    </>
                )}
            </div>
            {isExpanded && (
                <div className="space-y-0.5 ml-3 border-l border-stone-200 dark:border-stone-800 pl-1 mt-0.5">
                    {subCategories.map(sub => (
                        <SidebarBookmarkCategory 
                            key={sub.id} 
                            category={sub} 
                            bookmarks={bookmarks} 
                            allCategories={allCategories} 
                            expandedItems={expandedItems}
                            onToggleExpand={onToggleExpand}
                            onFileSelect={onFileSelect}
                            onCategorySelect={onCategorySelect}
                            allFiles={allFiles}
                            onUpdateCategory={onUpdateCategory}
                            onDeleteCategory={onDeleteCategory}
                        />
                    ))}
                    {categoryBookmarks.map(bm => (
                        <button 
                            key={bm.id} 
                            onClick={() => handleBookmarkClick(bm)}
                            className="w-full text-left px-3 py-1.5 text-[0.625rem] font-medium text-stone-600 dark:text-stone-400 hover:bg-amber-50 dark:hover:bg-amber-900/10 rounded-md transition-colors truncate flex items-center gap-2"
                        >
                            <i className="fa-solid fa-bookmark text-amber-500 text-[0.5rem] shrink-0 opacity-60"></i>
                            <span className="truncate">{(bm.title && bm.title.trim()) || bm.fileName || 'Untitled Bookmark'}</span>
                        </button>
                    ))}
                    {categoryBookmarks.length === 0 && subCategories.length === 0 && (
                        <p className="px-3 py-2 text-[0.5625rem] italic text-stone-400">Empty folder.</p>
                    )}
                </div>
            )}
        </div>
    );
};

const SidebarSubFolder: React.FC<{
    name: string;
    files: ArchiveFile[];
    selectedFileId?: string;
    onFileSelect?: (file: ArchiveFile) => void;
    isExpanded: boolean;
    onToggle: () => void;
    bookmarks?: Bookmark[];
    onOpenBookmarkDialog?: (title: string, fileId: string, fileName: string, paragraphId?: string, paragraphText?: string) => void;
}> = ({ name, files, selectedFileId, onFileSelect, isExpanded, onToggle, bookmarks, onOpenBookmarkDialog }) => {
    const [visibleCount, setVisibleCount] = React.useState(50);

    const handleShowMore = (e: React.MouseEvent) => {
        e.stopPropagation();
        setVisibleCount(prev => prev + 100);
    };

    const isBookmarked = (fileId: string) => {
        return bookmarks?.some(b => b.fileId === fileId && !b.paragraphId);
    };

    const visibleFiles = files.slice(0, visibleCount);
    const hasMore = visibleCount < files.length;

    return (
        <div className="select-none ml-4 border-l border-stone-200 dark:border-stone-800">
            <button 
                onClick={onToggle} 
                className={`w-full flex items-center justify-between px-3 py-1.5 text-[0.6875rem] transition-all hover:bg-stone-200/50 dark:hover:bg-white/5 ${isExpanded ? 'text-amber-500 font-bold' : 'text-stone-500'}`}
            >
                <div className="flex items-center gap-2 truncate">
                    <i className={`fa-solid ${isExpanded ? 'fa-folder-open' : 'fa-folder opacity-50'} text-[0.5625rem]`}></i>
                    <span className="truncate">{name}</span>
                    <span className="text-[0.5625rem] opacity-40 ml-1">({files.length})</span>
                </div>
            </button>
            {isExpanded && (
                <div className="bg-stone-50/50 dark:bg-black/5 pb-1">
                    {visibleFiles.map(file => {
                        const bookmarked = isBookmarked(file.id);
                        return (
                            <div key={file.id} className="group flex items-center">
                                <button 
                                    onClick={() => onFileSelect?.(file)} 
                                    className={`flex-1 text-left px-4 py-1.5 text-[0.625rem] pl-8 border-l-2 ${selectedFileId === file.id ? 'border-amber-500 bg-amber-100/30 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 font-semibold' : 'border-transparent text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`}
                                >
                                    {file.name}
                                </button>
                                {onOpenBookmarkDialog && (
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); onOpenBookmarkDialog(file.name, file.id, file.name); }}
                                        className={`pr-2 transition-all ${bookmarked ? 'text-amber-500 opacity-100 scale-110' : 'text-stone-300 opacity-0 group-hover:opacity-100 hover:text-amber-500 hover:scale-110'}`}
                                        title={bookmarked ? "Bookmarked in collection" : "Add to collection"}
                                    >
                                        <i className={`fa-solid fa-bookmark text-[0.625rem]`}></i>
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    {hasMore && (
                        <button onClick={handleShowMore} className="w-full text-center py-1.5 text-[0.5625rem] font-bold text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-colors uppercase tracking-wider">
                            Load More ({files.length - visibleCount})
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const SidebarFolder: React.FC<{
    name: string;
    files: ArchiveFile[];
    isExpanded: boolean;
    isActiveCategory: boolean;
    selectedFileId?: string;
    onToggle: () => void;
    onSelectCategory: () => void;
    onFileSelect?: (file: ArchiveFile) => void;
    hierarchical?: boolean;
    expandedItems: Set<string>;
    onToggleExpand: (id: string) => void;
    bookmarks?: Bookmark[];
    onOpenBookmarkDialog?: (title: string, fileId: string, fileName: string, paragraphId?: string, paragraphText?: string) => void;
}> = ({ name, files, isExpanded, isActiveCategory, selectedFileId, onToggle, onSelectCategory, onFileSelect, hierarchical, expandedItems, onToggleExpand, bookmarks, onOpenBookmarkDialog }) => {
    
    // Group sub-folders (100s) if hierarchical
    const subGroups = useMemo(() => {
        if (!hierarchical) return null;
        const groups: Record<string, ArchiveFile[]> = {};
        files.forEach(f => {
            const subName = f.summary || 'Other';
            if (!groups[subName]) groups[subName] = [];
            groups[subName].push(f);
        });
        return Object.keys(groups).sort((a, b) => {
            const aNum = a.match(/\d+/) ? parseInt(a.match(/\d+/)![0]) : 0;
            const bNum = b.match(/\d+/) ? parseInt(b.match(/\d+/)![0]) : 0;
            return aNum - bNum;
        }).map(k => ({ name: k, files: groups[k] }));
    }, [files, hierarchical]);

    const isBookmarked = (fileId: string) => {
        return bookmarks?.some(b => b.fileId === fileId && !b.paragraphId);
    };

    return (
        <div className="select-none">
            <button onClick={() => { onToggle(); onSelectCategory(); }} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-all border-l-4 ${isActiveCategory ? 'border-amber-500 text-amber-600 dark:text-amber-400 font-bold bg-amber-50 dark:bg-amber-900/10' : 'border-transparent text-stone-600 dark:text-stone-400 hover:bg-stone-200/50 dark:hover:bg-white/5'}`}>
                <div className="flex items-center gap-2 truncate">
                    <i className={`fa-solid ${isExpanded ? 'fa-folder-open text-amber-500' : 'fa-folder opacity-40'} text-[0.625rem]`}></i>
                    <span className="truncate">{name}</span>
                    <span className="text-[0.5625rem] text-stone-300 dark:text-stone-600 ml-1">({files.length})</span>
                </div>
                <i className={`fa-solid fa-caret-down text-[0.625rem] transition-transform ${isExpanded ? 'rotate-180' : ''}`}></i>
            </button>
            {isExpanded && (
                <div className="bg-stone-100/30 dark:bg-black/10 pb-1">
                    {hierarchical && subGroups ? (
                        subGroups.map(sub => (
                            <SidebarSubFolder 
                                key={sub.name} 
                                name={sub.name} 
                                files={sub.files} 
                                selectedFileId={selectedFileId} 
                                onFileSelect={onFileSelect} 
                                isExpanded={expandedItems.has(sub.name)}
                                onToggle={() => onToggleExpand(sub.name)}
                                bookmarks={bookmarks}
                                onOpenBookmarkDialog={onOpenBookmarkDialog}
                            />
                        ))
                    ) : (
                        files.slice(0, 100).map(file => {
                            const bookmarked = isBookmarked(file.id);
                            return (
                                <div key={file.id} className="group flex items-center">
                                    <button onClick={() => onFileSelect?.(file)} className={`flex-1 text-left px-4 py-1.5 text-[0.6875rem] pl-10 border-l-4 ${selectedFileId === file.id ? 'border-amber-500 bg-amber-100/50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 font-semibold' : 'border-transparent text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'}`}>
                                        {file.name}
                                    </button>
                                    {onOpenBookmarkDialog && (
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); onOpenBookmarkDialog(file.name, file.id, file.name); }}
                                            className={`pr-4 transition-all ${bookmarked ? 'text-amber-500 opacity-100 scale-110' : 'text-stone-300 opacity-0 group-hover:opacity-100 hover:text-amber-500 hover:scale-110'}`}
                                            title={bookmarked ? "Bookmarked in collection" : "Add to collection"}
                                        >
                                            <i className={`fa-solid fa-bookmark text-[0.625rem]`}></i>
                                        </button>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

const Sidebar: React.FC<SidebarProps> = ({ 
  activePage,
  onCategorySelect, 
  activeCategory, 
  allFiles = [],
  onFileSelect,
  onFileDelete,
  selectedFileId,
  threads,
  activeThreadId,
  onThreadSelect,
  onNewChat,
  onThreadDelete,
  user,
  // Controlled State Props
  expandedItems,
  onToggleExpand,
  sidebarSearch,
  onSearchChange,
  // Community
  communityGroups,
  communityFilter,
  communitySelectedGroupId,
  communityPosts,
  onCommunityFilterChange,
  onCommunityGroupSelect,
  onCommunityCreateGroup,
  onCommunityDiscoverGroups,
  bookmarks,
  onOpenBookmarkDialog,
  bookmarkCategories,
  onUpdateCategory,
  onDeleteCategory,
  onNavigate
}) => {
  
  // State for popover menu in Profile Sidebar
  const [openMenuFileId, setOpenMenuFileId] = useState<string | null>(null);
  const [isMyCollectionExpanded, setIsMyCollectionExpanded] = useState(true);
  const [isMyWikiExpanded, setIsMyWikiExpanded] = useState(true);

  const categoryFolders = useMemo(() => {
    const folders: Record<string, ArchiveFile[]> = {};
    // Disable sidebar search filter for Wiki page (context aware search is in header)
    const filtered = sidebarSearch.trim() && activePage !== 'wiki' 
      ? allFiles.filter(f => f.name.toLowerCase().includes(sidebarSearch.toLowerCase()))
      : allFiles;

    filtered.forEach(file => {
        const cat = file.category || 'General';
        if (!folders[cat]) folders[cat] = [];
        folders[cat].push(file);
    });

    return Object.keys(folders).sort((a, b) => {
      const aNum = a.match(/\d+/) ? parseInt(a.match(/\d+/)![0]) : 0;
      const bNum = b.match(/\d+/) ? parseInt(b.match(/\d+/)![0]) : 0;
      if (aNum && bNum) return aNum - bNum;
      return a.localeCompare(b);
    }).map(name => ({ name, files: folders[name] }));
  }, [allFiles, sidebarSearch, activePage]);

  const cleanTitle = (title: string) => {
      return title.replace(/[*_#`\[\]]/g, '').trim();
  };

  if (activePage === 'community' && user) {
      return (
          <CommunitySidebar 
             user={user}
             posts={communityPosts || []}
             groups={communityGroups || []}
             activeFilter={communityFilter || 'All'}
             selectedGroupId={communitySelectedGroupId || null}
             onFilterChange={onCommunityFilterChange || (() => {})}
             onGroupSelect={onCommunityGroupSelect || (() => {})}
             onCreateGroup={onCommunityCreateGroup || (() => {})}
             onDiscoverGroups={onCommunityDiscoverGroups || (() => {})}
          />
      );
  }

  if (activePage === 'chat') {
      return (
        <div className="w-full h-full flex flex-col bg-[#f2efe4] dark:bg-[#0f1521] border-r border-stone-200 dark:border-[#1e293b] text-stone-600 dark:text-stone-300 shrink-0 relative z-30 shadow-inner overflow-hidden transition-colors">
            <div className="p-4 border-b border-stone-200 dark:border-stone-800">
                <button onClick={onNewChat} className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg py-2.5 transition-colors shadow-sm text-sm font-bold">
                    <i className="fa-solid fa-plus"></i>
                    <span>New Chat</span>
                </button>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
                <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest px-4 py-2">Recent Chats</h3>
                {threads.length === 0 ? (
                    <p className="text-center text-xs text-stone-400 py-8 italic">No active conversations.</p>
                ) : (
                    threads.map(thread => (
                        <div key={thread.id} className={`group flex items-center gap-2 rounded-lg px-3 py-2.5 mb-1 cursor-pointer transition-colors ${activeThreadId === thread.id ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100' : 'hover:bg-stone-100 dark:hover:bg-stone-800'}`} onClick={() => onThreadSelect(thread)}>
                            <i className="fa-regular fa-comments text-xs opacity-50"></i>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{cleanTitle(thread.title)}</p>
                                <p className="text-[0.625rem] text-stone-400 truncate">{new Date(thread.lastUpdated).toLocaleDateString()}</p>
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); onThreadDelete(thread.id); }} className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-500 transition-opacity px-1">
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
      );
  }

  if (activePage === 'profile') {
      const myPages = allFiles?.filter(f => f.author === user?.email) || [];

      return (
        <div className="w-full h-full flex flex-col bg-[#f2efe4] dark:bg-[#0f1521] border-r border-stone-200 dark:border-[#1e293b] text-stone-600 dark:text-stone-300 shrink-0 relative z-30 shadow-inner overflow-hidden transition-colors">
            <div className="p-4 border-b border-stone-200 dark:border-stone-800">
                <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest py-3">
                    Account Menu
                </h3>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
                 <button 
                    onClick={() => onNavigate?.('/profile')}
                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-stone-200/50 dark:hover:bg-stone-800 transition-colors flex items-center gap-3 text-sm font-medium text-stone-700 dark:text-stone-200"
                 >
                    <i className="fa-solid fa-sliders text-amber-500 w-5"></i>
                    Preferences
                 </button>
                 <button 
                    onClick={() => onNavigate?.('/profile')}
                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-stone-200/50 dark:hover:bg-stone-800 transition-colors flex items-center gap-3 text-sm font-medium text-stone-700 dark:text-stone-200"
                 >
                    <i className="fa-solid fa-language text-amber-500 w-5"></i>
                    Language
                 </button>
                 <button 
                    onClick={() => onNavigate?.('/profile')}
                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-stone-200/50 dark:hover:bg-stone-800 transition-colors flex items-center gap-3 text-sm font-medium text-stone-700 dark:text-stone-200"
                 >
                    <i className="fa-solid fa-id-card text-amber-500 w-5"></i>
                    Account Info
                 </button>
                 
                 <div className="my-4 border-t border-stone-200 dark:border-stone-800"></div>
                 
                 <div className="px-4 py-2">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-[0.625rem] font-bold text-stone-400 uppercase tracking-widest">My Stats</p>
                    </div>
                    <div className="bg-white dark:bg-stone-800/50 rounded-lg p-3 border border-stone-100 dark:border-stone-700 space-y-2">
                        <button 
                            onClick={() => onNavigate?.('/chat')}
                            className="w-full flex justify-between items-center text-xs hover:text-amber-600 transition-colors cursor-pointer group"
                        >
                            <span className="group-hover:translate-x-1 transition-transform">Chats</span>
                            <span className="font-bold text-stone-800 dark:text-stone-200">{threads.length}</span>
                        </button>
                        <button 
                            onClick={() => {
                                if (activePage === 'profile') {
                                    onCategorySelect?.('my-wiki');
                                } else {
                                    onNavigate?.('/wiki');
                                }
                            }}
                            className="w-full flex justify-between items-center text-xs hover:text-amber-600 transition-colors cursor-pointer group"
                        >
                            <span className="group-hover:translate-x-1 transition-transform">Wiki Pages</span>
                            <span className="font-bold text-stone-800 dark:text-stone-200">{myPages.length}</span>
                        </button>
                        <button 
                            onClick={() => onCategorySelect?.('all-collections')}
                            className="w-full flex justify-between items-center text-xs mt-2 border-t border-stone-100 dark:border-stone-700 pt-2 hover:text-amber-600 transition-colors cursor-pointer group"
                        >
                            <span className="group-hover:translate-x-1 transition-transform">Bookmarks</span>
                            <span className="font-bold text-stone-800 dark:text-stone-200">{bookmarks?.length || 0}</span>
                        </button>
                    </div>
                 </div>

                 {/* My Collections Section */}
                 <div className="mt-4">
                    <button 
                        onClick={() => {
                            setIsMyCollectionExpanded(!isMyCollectionExpanded);
                            if (onCategorySelect) onCategorySelect('all-collections');
                        }}
                        className="w-full flex items-center justify-between px-4 py-3 text-[0.625rem] font-bold text-stone-400 uppercase tracking-widest hover:bg-stone-200/50 dark:hover:bg-stone-800 transition-colors rounded-lg group"
                    >
                        <span>My Collections</span>
                        <i className={`fa-solid fa-chevron-down transition-transform ${isMyCollectionExpanded ? 'rotate-180' : ''} opacity-0 group-hover:opacity-100`}></i>
                    </button>
                    {isMyCollectionExpanded && (
                        <div className="space-y-0.5 px-2">
                            {/* Render Categories */}
                            {bookmarkCategories && bookmarkCategories.filter((c: BookmarkCategory) => c.parentId === null).map((cat: BookmarkCategory) => (
                                <SidebarBookmarkCategory 
                                    key={cat.id}
                                    category={cat}
                                    bookmarks={bookmarks || []}
                                    allCategories={bookmarkCategories || []}
                                    expandedItems={expandedItems}
                                    onToggleExpand={onToggleExpand}
                                    onFileSelect={onFileSelect}
                                    onCategorySelect={onCategorySelect}
                                    onUpdateCategory={onUpdateCategory}
                                    onDeleteCategory={onDeleteCategory}
                                    allFiles={allFiles}
                                />
                            ))}

                            {/* Render General Bookmarks */}
                            {bookmarks && bookmarks.filter((bm: Bookmark) => !bm.categoryPath || bm.categoryPath === 'General' || !bookmarkCategories?.find((c: BookmarkCategory) => c.path === bm.categoryPath)).map((bm: Bookmark) => (
                                <button 
                                    key={bm.id} 
                                    onClick={() => {
                                        const file = allFiles?.find(f => f.id === bm.fileId);
                                        if (file && onFileSelect) onFileSelect(file);
                                    }}
                                    className="w-full text-left px-3 py-2 text-xs font-medium text-stone-600 dark:text-stone-400 hover:bg-stone-200/50 dark:hover:bg-stone-800 rounded-lg transition-colors truncate flex items-center gap-2"
                                >
                                    <i className="fa-solid fa-bookmark text-amber-500 mr-2 text-[0.625rem] shrink-0"></i>
                                    <span className="truncate">{(bm.title && bm.title.trim()) || bm.fileName || 'Untitled Bookmark'}</span>
                                </button>
                            ))}

                            {(!bookmarks || bookmarks.length === 0) && (
                                <p className="px-3 py-2 text-[0.625rem] italic text-stone-400">Empty collection.</p>
                            )}
                        </div>
                    )}
                 </div>

                 {myPages.length > 0 && (
                    <div className="mt-2">
                        <button 
                            onClick={() => {
                                setIsMyWikiExpanded(!isMyWikiExpanded);
                                if (onCategorySelect) onCategorySelect('my-wiki');
                            }}
                            className="w-full flex items-center justify-between px-4 py-3 text-[0.625rem] font-bold text-stone-400 uppercase tracking-widest hover:bg-stone-200/50 dark:hover:bg-stone-800 transition-colors rounded-lg group"
                        >
                            <span>My Wiki Pages</span>
                            <i className={`fa-solid fa-chevron-down transition-transform ${isMyWikiExpanded ? 'rotate-180' : ''} opacity-0 group-hover:opacity-100`}></i>
                        </button>
                        {isMyWikiExpanded && (
                            <div className="space-y-0.5 px-2">
                                {myPages.map(file => (
                                    <div key={file.id} className="group relative flex items-center justify-between hover:bg-stone-200/50 dark:hover:bg-stone-800 transition-colors pr-2 rounded-lg">
                                        <button 
                                            onClick={() => onFileSelect?.(file)} 
                                            className="flex-1 text-left px-3 py-2 text-xs font-medium text-stone-700 dark:text-stone-300 truncate"
                                        >
                                            <i className="fa-solid fa-file-lines text-stone-400 mr-2 text-[0.625rem]"></i>
                                            {file.name}
                                        </button>
                                        <div className="relative">
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); setOpenMenuFileId(openMenuFileId === file.id ? null : file.id); }}
                                                className={`p-1.5 rounded-md text-stone-400 hover:text-amber-600 hover:bg-stone-200 dark:hover:bg-stone-700 transition-all ${openMenuFileId === file.id ? 'opacity-100 text-amber-600 bg-stone-200 dark:bg-stone-700' : 'opacity-0 group-hover:opacity-100'}`}
                                            >
                                                <i className="fa-solid fa-ellipsis-vertical text-[0.625rem]"></i>
                                            </button>
                                            
                                            {openMenuFileId === file.id && (
                                                <>
                                                    {/* Backdrop to close */}
                                                    <div className="fixed inset-0 z-40" onClick={() => setOpenMenuFileId(null)}></div>
                                                    
                                                    <div className="absolute right-0 top-full mt-1 w-28 bg-white dark:bg-stone-800 rounded-lg shadow-xl border border-stone-200 dark:border-stone-700 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-100 py-1">
                                                        <button 
                                                            onClick={() => { onFileSelect?.(file); setOpenMenuFileId(null); }} 
                                                            className="w-full text-left px-3 py-2 text-[0.625rem] font-bold hover:bg-stone-100 dark:hover:bg-stone-700 text-stone-600 dark:text-stone-300 flex items-center gap-2"
                                                        >
                                                            <i className="fa-solid fa-pen text-amber-500"></i> Edit
                                                        </button>
                                                        <div className="h-px bg-stone-100 dark:bg-stone-700 my-0.5"></div>
                                                        <button 
                                                            onClick={() => { if(window.confirm('Are you sure you want to delete this page?')) { onFileDelete?.(file.id); } setOpenMenuFileId(null); }} 
                                                            className="w-full text-left px-3 py-2 text-[0.625rem] font-bold hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 flex items-center gap-2"
                                                        >
                                                            <i className="fa-solid fa-trash"></i> Delete
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                 )}
            </div>
        </div>
      );
  }

  return (
    <div className="w-full h-full flex flex-col bg-[#f2efe4] dark:bg-[#0f1521] border-r border-stone-200 dark:border-[#1e293b] text-stone-600 dark:text-stone-300 shrink-0 relative z-30 shadow-inner overflow-hidden transition-colors">
      <nav className="py-2 flex-1 flex flex-col overflow-hidden">
          {activePage === 'readings' && (
              <div className="px-4 pb-4 border-b border-stone-200 dark:border-stone-800">
                  <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest py-3">
                      Readings Archive
                  </h3>
                  <div className="relative">
                      <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-xs text-stone-400"></i>
                      <input 
                          type="text" 
                          placeholder="Filter records..."
                          value={sidebarSearch}
                          onChange={(e) => onSearchChange(e.target.value)}
                          className="w-full bg-white dark:bg-stone-800/50 border border-stone-200 dark:border-stone-700 rounded-lg pl-9 pr-3 py-1.5 text-xs outline-none focus:border-amber-500 transition-colors"
                      />
                  </div>
              </div>
          )}
          <div className="flex-1 overflow-y-auto custom-scrollbar py-2">
              {/* Akashic Wiki Reset Button for Wiki Page */}
              {activePage === 'wiki' && (
                  <button 
                      onClick={() => onCategorySelect('all')}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-all border-l-4 ${activeCategory === 'all' ? 'border-amber-500 text-amber-600 dark:text-amber-400 font-bold bg-amber-50 dark:bg-amber-900/10' : 'border-transparent text-stone-600 dark:text-stone-400 hover:bg-stone-200/50 dark:hover:bg-white/5'}`}
                  >
                      <i className={`fa-solid ${activeCategory === 'all' ? 'fa-book-open text-amber-500' : 'fa-book opacity-40'} text-[0.625rem]`}></i>
                      <span className="truncate">Akashic Wiki</span>
                  </button>
              )}

              {categoryFolders.map(folder => (
                  <SidebarFolder
                      key={folder.name}
                      name={folder.name}
                      files={folder.files}
                      isExpanded={expandedItems.has(folder.name)}
                      isActiveCategory={activeCategory === folder.name}
                      selectedFileId={selectedFileId}
                      onToggle={() => onToggleExpand(folder.name)}
                      onSelectCategory={() => onCategorySelect(folder.name)}
                      onFileSelect={onFileSelect}
                      hierarchical={activePage === 'readings'}
                      expandedItems={expandedItems}
                      onToggleExpand={onToggleExpand}
                      bookmarks={bookmarks}
                      onOpenBookmarkDialog={onOpenBookmarkDialog}
                  />
              ))}
          </div>
      </nav>
    </div>
  );
};

export default Sidebar;
